import type { Dispatch, SetStateAction } from 'react'
import { useState } from 'react'

import ArrowDown from '@Icons/arrow-down-sort.svg?react'
import FilterLines from '@Icons/filter-lines.svg?react'
import SearchIcon from '@Icons/search-lg-blue.svg?react'
import { useMeQuery } from '@Store/api'
import { useAppDispatch, useAppSelector } from '@Store/hooks'
import { setProductCompetition } from '@Store/slices/settingsSlice'
import type { ProductsFilterType } from '@Store/types'
import { PremiumFeatures } from '@Store/types/billing'
import type { Filter } from '@Store/types/product'
import { useNavigateBillingModal } from '@Utils/context/modal/use-navigate-billing-modal'

import FilterConditionBuilder from './filter/condition-builder'
import SavedFilters from './filter/saved-filters'

type ProductSearchProps = {
  filters: ProductsFilterType
  onSort: (sortBy: Sort) => void
  sortBy: Sort
  onSearch: (search: string) => void
  foundedProducts: number
  panelVisible: boolean | null
  onFilterOpenClick: () => void
  listSavedProductFilters?: Filter[]
  isListSavedProductFiltersLoading: boolean
  selectedQuickFilterId?: number | string
  selectedQuickFilterName?: string
  onSelectSavedFilter: (filter: any) => void
  onApplyFilters: (filters: ProductsFilterType) => void
  onResetFilters: (refetch?: boolean) => void
  onSaveFilter: (values: any, url?: string) => void
  setPanelVisible: Dispatch<SetStateAction<boolean | null>>
}

type Sort = {
  field: string
  isDesc: boolean
}

function ProductSearchWithLoading({
  isInitiated = false,
  ...props
}: { isInitiated?: boolean } & ProductSearchProps): JSX.Element {
  if (!isInitiated) {
    return <ProductSearchLoading />
  }
  return <ProductSearch {...props} />
}

export default ProductSearchWithLoading

function ProductSearch({
  filters,
  onSort,
  sortBy,
  onSearch,
  panelVisible,
  onFilterOpenClick,
  listSavedProductFilters,
  isListSavedProductFiltersLoading,
  selectedQuickFilterId,
  selectedQuickFilterName,
  onSelectSavedFilter,
  onApplyFilters,
  onResetFilters,
  onSaveFilter,
  setPanelVisible,
}: ProductSearchProps): JSX.Element {
  const { data: me } = useMeQuery()
  const { open } = useNavigateBillingModal()
  const dispatch = useAppDispatch()
  const showProductList = useAppSelector(
    (state) => state.settings.show_product_competition_list,
  )
  const [searchValue, setSearchValue] = useState('')

  const onSortBy = (field: 'matched' | 'name' | 'date_created') => (): void => {
    onSort({
      field: field,
      isDesc: sortBy?.field === field ? !sortBy.isDesc : false,
    })
  }

  return (
    <div id="filter-area" className="flex flex-col w-full">
      {/* Saved filters row — always visible */}
      <div className="flex flex-wrap items-center justify-between gap-2 pb-4">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {(listSavedProductFilters?.length ?? 0) > 0 && (
            <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
              Saved Filters (collection)
            </span>
          )}
          <SavedFilters
            data={listSavedProductFilters}
            isLoading={isListSavedProductFiltersLoading}
            onSelect={onSelectSavedFilter}
            selectedItemId={selectedQuickFilterId}
          />
        </div>
        <button
          onClick={onFilterOpenClick}
          className={`h-10 border border-solid rounded-lg flex items-center justify-center px-4 shrink-0 transition-colors ${
            panelVisible
              ? 'border-primary-500 bg-primary-50 text-primary-700'
              : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
          }`}
        >
          <FilterLines
            className={panelVisible ? 'text-primary-600' : 'text-gray-500'}
          />
          <span className="px-2.5 font-medium text-sm whitespace-nowrap">
            New Filter
          </span>
        </button>
      </div>

      {panelVisible ? (
        /* Filter open: condition builder */
        <FilterConditionBuilder
          key={selectedQuickFilterId ?? 'new'}
          initialFilters={filters}
          filterName={selectedQuickFilterName}
          filterId={selectedQuickFilterId}
          onClose={onFilterOpenClick}
          onApply={onApplyFilters}
          onReset={onResetFilters}
          onSaveFilter={onSaveFilter}
          setPanelVisible={setPanelVisible}
        />
      ) : (
        /* Filter closed: search + sort */
        <>
          <form
            onSubmit={(e) => {
              e.preventDefault()
              onSearch(searchValue)
            }}
            className="flex flex-row w-full text-gray-500 border border-gray-300 rounded-lg py-[10px] items-center px-[14px] mb-6"
          >
            <SearchIcon className="shrink-0 mr-2 text-gray-400" />
            <input
              type="text"
              className="w-full focus:ring-0 !outline-none"
              placeholder="Search"
              onChange={(e) => setSearchValue(e.target.value)}
              value={searchValue || ''}
            />
          </form>

          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 pb-4">
            <div className="flex flex-row h-10">
              <button
                className={`border border-solid border-gray-300 rounded-l-lg hover:bg-gray-50 flex items-center gap-0.5 pl-2 pr-3${sortBy?.field === 'matched' ? ' bg-primary-100' : ''}`}
                type="button"
                onClick={onSortBy('matched')}
              >
                <ArrowDown
                  className={
                    sortBy?.field === 'matched' && sortBy.isDesc
                      ? 'rotate-180'
                      : 'rotate-0'
                  }
                />
                <span className="font-medium text-sm text-gray-700 whitespace-nowrap">
                  Matched <span className="hidden sm:inline">First</span>
                </span>
              </button>
              <button
                className={`border border-solid border-l-0 border-gray-300 hover:bg-gray-50 flex items-center gap-0.5 pl-2 pr-3${sortBy?.field === 'name' ? ' bg-primary-100' : ''}`}
                type="button"
                onClick={onSortBy('name')}
              >
                <ArrowDown
                  className={
                    sortBy?.field === 'name' && sortBy.isDesc
                      ? 'rotate-180'
                      : 'rotate-0'
                  }
                />
                <span className="font-medium text-sm text-gray-700 whitespace-nowrap">
                  A to Z
                </span>
              </button>
              <button
                className={`border border-solid border-l-0 border-gray-300 rounded-r-lg hover:bg-gray-50 flex items-center gap-0.5 pl-2 pr-3${sortBy?.field === 'date_created' ? ' bg-primary-100' : ''}`}
                onClick={onSortBy('date_created')}
              >
                <ArrowDown
                  className={
                    sortBy?.field === 'date_created' && sortBy.isDesc
                      ? 'rotate-180'
                      : 'rotate-0'
                  }
                />
                <span className="font-medium text-sm text-gray-700 whitespace-nowrap">
                  Date <span className="hidden sm:inline">Added</span>
                </span>
              </button>
            </div>
            <div className="flex flex-row items-center">
              <button
                className={`border border-solid border-gray-300 rounded-l-lg h-10 px-3 font-medium text-sm text-gray-700 whitespace-nowrap ${
                  !showProductList ? 'bg-gray-100' : 'bg-white hover:bg-gray-50'
                }`}
                type="button"
                id="productIndexView"
                onClick={() => {
                  if (me && !PremiumFeatures.includes(me.package_type)) {
                    open('Price Analytics')
                  } else {
                    dispatch(setProductCompetition(false))
                  }
                }}
              >
                Product Index <span className="hidden xl:inline">View</span>
              </button>
              <button
                className={`border border-solid border-l-0 border-gray-300 rounded-r-lg h-10 px-3 font-medium text-sm text-gray-700 whitespace-nowrap ${
                  showProductList ? 'bg-gray-100' : 'bg-white hover:bg-gray-50'
                }`}
                type="button"
                id="competitionListView"
                onClick={() => {
                  dispatch(setProductCompetition(true))
                }}
              >
                Competition List <span className="hidden xl:inline">View</span>
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}

function ProductSearchLoading(): JSX.Element {
  return (
    <div className="flex flex-row justify-between w-full">
      <div className="flex flex-col w-3/5 space-y-8">
        <div className="flex flex-col space-y-4">
          <div className="flex flex-row space-x-2 w-full">
            <div className="animate-pulse bg-gray-200 h-10 w-1/3 rounded-lg"></div>
            <div className="animate-pulse bg-gray-200 h-10 w-1/3 rounded-lg"></div>
            <div className="animate-pulse bg-gray-200 h-10 w-1/3 rounded-lg"></div>
          </div>
          <div className="animate-pulse bg-gray-200 h-10 w-5/6 rounded-lg"></div>
        </div>
        <div className="flex flex-row space-x-2 w-9/12">
          <div className="animate-pulse bg-gray-200 h-10 w-1/3 rounded-lg"></div>
          <div className="animate-pulse bg-gray-200 h-10 w-1/3 rounded-lg"></div>
          <div className="animate-pulse bg-gray-200 h-10 w-1/3 rounded-lg"></div>
        </div>
      </div>
      <div className="flex flex-col w-2/5 space-y-8 items-end">
        <div className="flex flex-col space-y-4 items-end w-full">
          <div className="animate-pulse bg-gray-200 h-10 w-4/6 rounded-lg"></div>
          <div className="flex flex-row space-x-3 w-full">
            <div className="animate-pulse bg-gray-200 h-10 w-2/3 rounded-lg"></div>
            <div className="animate-pulse bg-gray-200 h-10 w-1/3 rounded-lg"></div>
          </div>
        </div>
        <div className="animate-pulse bg-gray-200 h-10 w-5/6 rounded-lg"></div>
      </div>
    </div>
  )
}
